import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { LifeBuoy, Mail, Bug, Lightbulb } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const KINDS = [
    { v: 'help', label: 'I need help', icon: LifeBuoy },
    { v: 'bug', label: 'Report a bug', icon: Bug },
    { v: 'idea', label: 'Share an idea', icon: Lightbulb },
];

export default function Help() {
    useSEO({
        title: 'Help & support — Linguistic Quest',
        description: 'Get help with Linguistic Quest. Report a bug, request a feature, or contact support.',
        path: '/help',
    });
    const loc = useLocation();
    const [kind, setKind] = useState('help');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            await api.post('/help/feedback', {
                kind, subject, message, page: loc.state?.from || document.referrer || '/help',
            });
            setSent(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not send — you can always email feedback@linguisticquest.com directly.');
        } finally { setBusy(false); }
    };

    return (
        <main className="max-w-2xl mx-auto px-6 py-12" data-testid="page-help">
            <div className="flex items-center gap-3">
                <LifeBuoy size={28} className="text-lq-purple-500"/>
                <h1 className="font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">How can we help?</h1>
            </div>
            <p className="mt-2 text-lq-ink-700 max-w-xl">A human at <a href="mailto:feedback@linguisticquest.com" className="text-lq-purple-700 hover:underline">feedback@linguisticquest.com</a> reads every message. We aim to reply within two working days.</p>
            <p className="mt-1 text-sm text-lq-ink-500">Looking for quick answers first? <Link to="/faq" className="text-lq-purple-700 hover:underline">Browse the FAQ</Link>.</p>

            {sent ? (
                <div className="mt-8 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-6 text-center" data-testid="help-sent">
                    <p className="font-display text-lg text-lq-ink-900">Message on its way. Thank you.</p>
                    <p className="mt-1 text-sm text-lq-ink-700">We'll be in touch soon. You can close this page safely.</p>
                    <Link to="/" className="mt-4 inline-block text-lq-purple-700 font-semibold hover:underline">Back home</Link>
                </div>
            ) : (
                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="help-form">
                    <div>
                        <span className="text-sm font-medium text-lq-ink-900">What is it about?</span>
                        <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup">
                            {KINDS.map((k) => {
                                const Icon = k.icon;
                                const on = kind === k.v;
                                return (
                                    <button type="button" key={k.v} onClick={() => setKind(k.v)}
                                        className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl text-sm border-2 transition-colors ${on ? 'bg-lq-purple-50 border-lq-purple-500 text-lq-purple-900' : 'bg-white border-lq-ink-100 text-lq-ink-700 hover:border-lq-purple-300'}`}
                                        data-testid={`help-kind-${k.v}`} role="radio" aria-checked={on}>
                                        <Icon size={18}/> {k.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <label className="block">
                        <span className="text-sm font-medium text-lq-ink-900">Subject</span>
                        <input required maxLength={140} value={subject} onChange={(e) => setSubject(e.target.value)}
                            className="mt-1 w-full px-4 py-3 rounded-xl border border-lq-ink-300 bg-white focus:border-lq-purple-500 outline-none"
                            data-testid="help-subject"/>
                    </label>
                    <label className="block">
                        <span className="text-sm font-medium text-lq-ink-900">Your message</span>
                        <textarea required maxLength={4000} rows={6} value={message} onChange={(e) => setMessage(e.target.value)}
                            className="mt-1 w-full px-4 py-3 rounded-xl border border-lq-ink-300 bg-white focus:border-lq-purple-500 outline-none"
                            placeholder="Tell us what's on your mind — no need for tech jargon."
                            data-testid="help-message"/>
                    </label>
                    {err && <p className="text-sm text-lq-error">{err}</p>}
                    <div className="flex flex-wrap gap-3">
                        <button type="submit" disabled={busy} className="px-6 py-3 rounded-full bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold disabled:opacity-60" data-testid="help-submit">
                            {busy ? 'Sending…' : 'Send message'}
                        </button>
                        <a href="mailto:feedback@linguisticquest.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-lq-ink-300 text-lq-ink-900 hover:border-lq-purple-300" data-testid="help-mailto">
                            <Mail size={16}/> Or email us instead
                        </a>
                    </div>
                </form>
            )}
        </main>
    );
}
